/**
 * Present notifications with localized copy + actionable deep-links from category.
 * Stored title/message stay English/canonical; the SPA re-localizes for the viewer's UI language.
 * Falls back to stored title/message for unknown categories.
 */

import i18n from '../i18n';
import { looksLikeUnlocalizedEnglish } from './apiError';

const CATEGORY = {
  LOW_WALLET: 'LOW_WALLET',
  LOW_WALLET_DIGEST: 'LOW_WALLET_DIGEST',
  ABSENCE: 'ABSENCE',
  DSAR_REQUEST: 'DSAR_REQUEST',
  CHESS: 'CHESS',
  CHESS_CHALLENGE: 'CHESS_CHALLENGE',
  CHESS_GAME: 'CHESS_GAME',
  CHESS_TOURNAMENT: 'CHESS_TOURNAMENT',
  CHESS_FAIR_PLAY: 'CHESS_FAIR_PLAY',
  CHESS_CORR: 'CHESS_CORR',
  CHESS_HOMEWORK: 'CHESS_HOMEWORK',
};

function isChessCategory(category) {
  return category === 'CHESS' || String(category || '').startsWith('CHESS_');
}

function parseLowWallet(message = '') {
  const patterns = [
    /^(.+?)'s wallet is at (.+?) \(threshold (.+?)\)$/i,
    /^(.+?) is at (.+?) \(threshold (.+?)\)/i,
    /^Wallet von (.+?) steht bei (.+?) \(Schwelle (.+?)\)/i,
    /^(.+?) : solde (.+?) \(seuil (.+?)\)/i,
    /^Wallet van (.+?) staat op (.+?) \(drempel (.+?)\)/i,
  ];
  for (const re of patterns) {
    const m = message.match(re);
    if (m) return { studentName: m[1], balance: m[2], threshold: m[3] };
  }
  return null;
}

function parseAbsence(message = '') {
  const patterns = [
    /^(.+?) was marked absent for session on (.+)$/i,
    /^(.+?) wurde für die Sitzung am (.+?) als abwesend markiert/i,
    /^(.+?) a été marqué absent pour la séance du (.+)$/i,
    /^(.+?) is als afwezig gemarkeerd voor de sessie op (.+)$/i,
  ];
  for (const re of patterns) {
    const m = message.match(re);
    if (m) return { studentName: m[1], sessionInfo: m[2] };
  }
  return null;
}

function parseDsarMessage(message = '') {
  const patterns = [
    /^(.+?) \(user #(\d+), (.+?)\) requested a copy of their personal data/i,
    /^(.+?) \(Benutzer #(\d+), (.+?)\) hat eine Kopie der personenbezogenen Daten/i,
    /^(.+?) \(utilisateur #(\d+), (.+?)\) a demandé une copie de ses données/i,
    /^(.+?) \(gebruiker #(\d+), (.+?)\) heeft een kopie van de persoonsgegevens/i,
  ];
  for (const re of patterns) {
    const m = message.match(re);
    if (m) return { name: m[1], userId: m[2], username: m[3] };
  }
  return null;
}

function linkParams(link) {
  if (!link) return {};
  try {
    const url = new URL(link, 'http://local.invalid');
    return {
      userId: url.searchParams.get('userId') || undefined,
      username: url.searchParams.get('username') || undefined,
      name: url.searchParams.get('name') || undefined,
      studentId: url.searchParams.get('studentId') || undefined,
    };
  } catch {
    return {};
  }
}

function parseDsar(item) {
  const fromLink = linkParams(item?.link);
  if (fromLink.userId && (fromLink.name || fromLink.username)) {
    return {
      name: fromLink.name || fromLink.username,
      userId: fromLink.userId,
      username: fromLink.username || fromLink.name || '',
    };
  }
  const fromDedupe = item?.dedupeKey?.match(/^DSAR_REQUEST_(\d+)/i)?.[1];
  const fromMsg = parseDsarMessage(item?.message || '');
  if (fromMsg) return fromMsg;
  if (fromDedupe) {
    return {
      name: fromLink.name || fromLink.username || `#${fromDedupe}`,
      userId: fromDedupe,
      username: fromLink.username || '',
    };
  }
  return null;
}

function parseDigestCount(title = '', message = '') {
  const patterns = [
    /^(\d+)\s+students/i,
    /^(\d+)\s+Schüler/i,
    /^(\d+)\s+élèves/i,
    /^(\d+)\s+leerlingen/i,
  ];
  for (const text of [title, message]) {
    for (const re of patterns) {
      const m = text.match(re);
      if (m) return Number(m[1]);
    }
  }
  return null;
}

function rolePrefix(role) {
  const r = String(role || 'ADMIN').toUpperCase();
  if (r === 'MANAGER') return '/manager';
  if (r === 'COACH') return '/coach';
  if (r === 'STUDENT') return '/student';
  return '/admin';
}

function adjustRolePrefix(link, prefix) {
  if (!link) return null;
  return link.replace(/^\/(admin|manager|coach|student)(?=\/|$|\?)/, prefix);
}

function studentIdFromItem(item) {
  const fromDedupe = item?.dedupeKey?.match(/STUDENT:(\d+)/i)?.[1];
  if (fromDedupe) return fromDedupe;
  const fromLink = linkParams(item?.link).studentId;
  return fromLink || null;
}

function dsarUserIdFromItem(item) {
  const parsed = parseDsar(item);
  if (parsed?.userId) return parsed.userId;
  const fromDedupe = item?.dedupeKey?.match(/^DSAR_REQUEST_(\d+)/i)?.[1];
  return fromDedupe || null;
}

/**
 * Build a useful destination for a notification click.
 * Rewrites legacy dead links (e.g. /admin/reports?studentId=…) for existing rows.
 */
export function resolveNotificationDestination(item, role = 'ADMIN') {
  const prefix = rolePrefix(role);
  const category = (item?.category || '').toUpperCase();
  const studentId = studentIdFromItem(item);

  if (category === CATEGORY.LOW_WALLET && studentId) {
    return `${prefix}/advance-payment?studentId=${studentId}`;
  }

  if (category === CATEGORY.LOW_WALLET_DIGEST) {
    if (String(role || '').toUpperCase() === 'MANAGER') {
      return `${prefix}/advance-payment`;
    }
    return `${prefix}/reports?tab=payments-due`;
  }

  if (category === CATEGORY.DSAR_REQUEST) {
    const parsed = parseDsar(item);
    const userId = dsarUserIdFromItem(item);
    const params = new URLSearchParams({ tab: 'data-export' });
    if (userId) params.set('userId', userId);
    if (parsed?.name) params.set('name', parsed.name);
    if (parsed?.username) params.set('username', parsed.username);
    const roleFromLink = linkParams(item?.link).role;
    if (roleFromLink) params.set('role', roleFromLink);
    return `/admin/reports?${params.toString()}`;
  }

  if (category === CATEGORY.ABSENCE && item?.link) {
    return adjustRolePrefix(item.link, prefix);
  }

  // Legacy: /admin/reports?studentId=42 → wallet top-up
  const legacyWallet = item?.link?.match(/^\/(admin|manager)\/reports\?studentId=(\d+)/i);
  if (legacyWallet) {
    return `/${legacyWallet[1].toLowerCase()}/advance-payment?studentId=${legacyWallet[2]}`;
  }

  // Legacy tournament / club-night / puzzle-homework deep links → playing hub (UI removed).
  if (
    category === CATEGORY.CHESS_TOURNAMENT
    || category === CATEGORY.CHESS_HOMEWORK
    || (item?.link && String(item.link).includes('/playing/tournaments'))
    || (item?.link && String(item.link).includes('/playing/puzzles'))
  ) {
    return '/playing';
  }

  if (item?.link) {
    return adjustRolePrefix(item.link, prefix);
  }

  return null;
}

/**
 * @returns {{ title, message, actionLabel, categoryLabel, severity }}
 */
export function formatNotification(item, t) {
  const category = (item?.category || '').toUpperCase();
  const severity = item?.type || 'INFO';
  const fallback = {
    title: item?.title || t('notifications.content.fallbackTitle'),
    message: item?.message || '',
    actionLabel: item?.link ? t('notifications.content.open') : null,
    categoryLabel: t('notifications.categories.general'),
    severity,
  };

  if (category === CATEGORY.LOW_WALLET) {
    const p = parseLowWallet(item.message);
    return {
      title: t('notifications.content.lowWallet.title'),
      message: p
        ? t('notifications.content.lowWallet.body', p)
        : item.message,
      actionLabel: t('notifications.content.lowWallet.action'),
      categoryLabel: t('notifications.categories.billing'),
      severity,
    };
  }

  if (category === CATEGORY.LOW_WALLET_DIGEST) {
    const count = parseDigestCount(item.title, item.message) || 0;
    return {
      title: t('notifications.content.lowWalletDigest.title', { count }),
      message: t('notifications.content.lowWalletDigest.body', { count }),
      actionLabel: t('notifications.content.lowWalletDigest.action'),
      categoryLabel: t('notifications.categories.billing'),
      severity,
    };
  }

  if (category === CATEGORY.ABSENCE) {
    const p = parseAbsence(item.message);
    return {
      title: t('notifications.content.absence.title'),
      message: p
        ? t('notifications.content.absence.body', p)
        : item.message,
      actionLabel: t('notifications.content.absence.action'),
      categoryLabel: t('notifications.categories.attendance'),
      severity,
    };
  }

  if (category === CATEGORY.DSAR_REQUEST) {
    const p = parseDsar(item);
    return {
      title: t('notifications.content.dsar.title'),
      message: p
        ? t('notifications.content.dsar.body', p)
        : item.message,
      actionLabel: t('notifications.content.dsar.action'),
      categoryLabel: t('notifications.categories.privacy'),
      severity,
    };
  }

  if (isChessCategory(category)) {
    let categoryLabel = t('notifications.categories.chess', { defaultValue: 'Chess' });
    if (category === CATEGORY.CHESS_CHALLENGE) {
      categoryLabel = t('notifications.categories.chessChallenge', { defaultValue: 'Challenge' });
    } else if (category === CATEGORY.CHESS_GAME) {
      categoryLabel = t('notifications.categories.chessGame', { defaultValue: 'Game' });
    } else if (category === CATEGORY.CHESS_FAIR_PLAY) {
      categoryLabel = t('notifications.categories.chessFairPlay', { defaultValue: 'Fair play' });
    } else if (category === CATEGORY.CHESS_CORR) {
      categoryLabel = t('notifications.categories.chessCorrespondence', { defaultValue: 'Correspondence' });
    }
    // Legacy CHESS_TOURNAMENT / CHESS_HOMEWORK rows keep the generic chess label (UI removed).

    const uiLang = String(i18n.language || 'en').split('-')[0].toLowerCase();
    let title = item?.title || t('notifications.content.fallbackTitle');
    if (uiLang !== 'en') {
      title = t(`notifications.content.chessGeneric.${category}`, {
        defaultValue: item?.title || title,
      });
    }
    let message = item?.message || '';
    if (uiLang !== 'en' && looksLikeUnlocalizedEnglish(message)) {
      message = t('notifications.content.chessGeneric.bodyFallback', { defaultValue: message });
    }

    return {
      title,
      message,
      actionLabel: item?.link
        ? (String(item.link).includes('analyze=1')
          ? t('notifications.content.reviewGame', { defaultValue: 'Review game' })
          : t('notifications.content.open'))
        : null,
      categoryLabel,
      severity,
    };
  }

  return fallback;
}

export { CATEGORY };
